import { Search, ScanLine } from "lucide-react";

import { strings } from "@perceptron/constants";
import { Button } from "@perceptron/ui/components/button";
import { Input } from "@perceptron/ui/components/input";
import { cn } from "@perceptron/utils/cn";

import type { TimelinePlayerBridge } from "../../bridge";
import { Checkbox } from "./Checkbox";
import { useTrackingInspectorStore } from "./controller";
import { TRACKING_SOURCE_APPEARANCES } from "./sourceAppearance";
import { TrackDetail } from "./TrackDetail";

const copy = strings.trackingInspector;

export function InspectPanel({
  bridge,
  search,
  onSearchChange,
}: {
  bridge: TimelinePlayerBridge;
  search: string;
  onSearchChange: (search: string) => void;
}) {
  const snapshot = useTrackingInspectorStore((state) => state.snapshot);
  const filter = useTrackingInspectorStore((state) => state.filter);
  const selectedTrackId = useTrackingInspectorStore((state) => state.selectedTrackId);
  const showSources = useTrackingInspectorStore((state) => state.showSources);
  const setFilter = useTrackingInspectorStore((state) => state.setFilter);
  const selectTrack = useTrackingInspectorStore((state) => state.selectTrack);
  const setShowSources = useTrackingInspectorStore((state) => state.setShowSources);
  const query = search.trim().toLocaleLowerCase();
  const allRows = snapshot?.rows ?? [];
  const problems = allRows.filter(
    (row) => row.status === "error" || row.status === "rejected" || row.warnings.length > 0,
  );
  const rows = (filter === "all" ? allRows : problems).filter(
    (row) => !query || (row.label + " " + row.trackId).toLocaleLowerCase().includes(query),
  );
  const selected = rows.find((row) => row.trackId === selectedTrackId) ?? rows[0];
  const emptyMessage = !snapshot
    ? copy.waitingForData
    : query
      ? copy.noMatchingTracks
      : filter === "problems"
        ? copy.noProblems
        : copy.noTracks;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-2.5">
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-foreground-secondary">{copy.currentFrame}</span>
          <span className="font-medium tabular-nums">{snapshot ? snapshot.currentTime.toFixed(3) + "s" : "—"}</span>
        </div>
        <Checkbox checked={showSources} label={copy.showSources} onChange={setShowSources} />
      </div>
      {snapshot?.workerError && (
        <p className="shrink-0 border-b border-border bg-background-error px-4 py-2 text-xs" role="alert">
          {copy.workerError}: {snapshot.workerError}
        </p>
      )}
      <div className="flex shrink-0 flex-wrap items-center gap-2 px-4 py-3">
        <div aria-label={copy.filter} className="flex rounded-md bg-background-alt-2 p-0.5" role="group">
          {(["all", "problems"] as const).map((value) => (
            <Button
              aria-label={copy[value]}
              aria-pressed={filter === value}
              className={cn("h-7 gap-1.5 px-2 text-xs", filter === value && "bg-blue-faded text-brand-blue")}
              key={value}
              size="sm"
              type="button"
              variant="ghost"
              onClick={() => setFilter(value)}
            >
              {copy[value]}
              <span aria-hidden="true" className="text-xs tabular-nums opacity-70">
                {value === "all" ? allRows.length : problems.length}
              </span>
            </Button>
          ))}
        </div>
        <div className="relative ml-auto min-w-32 flex-1 [@container(min-width:520px)]:max-w-52">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-2 top-2 size-3.5 text-foreground-secondary"
          />
          <Input
            aria-label={copy.searchTracks}
            className="h-8 pl-7 text-xs shadow-none"
            placeholder={copy.searchTracks}
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </div>
      </div>
      {rows.length > 0 && (
        <label className="mx-4 mb-3 flex shrink-0 flex-col gap-1 [@container(min-width:520px)]:hidden">
          <span className="sr-only">{copy.track}</span>
          <select
            className="h-9 w-full min-w-0 rounded-md border border-border bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={selected?.trackId}
            onChange={(event) => selectTrack(event.target.value)}
          >
            {rows.map((row) => (
              <option key={row.trackId} value={row.trackId}>
                {row.label}: {row.title}
              </option>
            ))}
          </select>
        </label>
      )}
      {snapshot && selected ? (
        <div className="grid min-h-0 flex-1 [@container(min-width:520px)]:grid-cols-[164px_minmax(0,1fr)]">
          <ul
            aria-label={copy.tracks}
            className="hidden min-h-0 space-y-1 overflow-y-auto overscroll-contain border-r border-border px-2 pb-3 [@container(min-width:520px)]:block"
          >
            {rows.map((row) => (
              <li key={row.trackId}>
                <Button
                  aria-label={row.label + ": " + row.title}
                  aria-pressed={row.trackId === selected.trackId}
                  className={cn(
                    "h-auto min-h-12 w-full justify-start gap-2 whitespace-normal rounded-md border border-transparent px-2 py-2 text-left text-sm font-normal",
                    row.trackId === selected.trackId && "border-brand-blue bg-blue-faded text-foreground",
                  )}
                  data-tracking-inspector-row={row.trackId}
                  data-tracking-status={row.status}
                  type="button"
                  variant="ghost"
                  onClick={() => selectTrack(row.trackId)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block break-words font-medium">{row.label}</span>
                    <span className="mt-1 flex items-start gap-1.5 text-xs text-foreground-secondary">
                      <span
                        aria-hidden="true"
                        className={cn(
                          "mt-1.5 size-1 shrink-0 rounded-full bg-foreground-secondary",
                          (row.status === "error" || row.status === "rejected") && "bg-error",
                        )}
                      />
                      {row.title}
                    </span>
                  </span>
                </Button>
              </li>
            ))}
          </ul>
          <div className="min-h-0 min-w-0 overflow-y-auto overscroll-contain px-4 pb-4">
            <TrackDetail bridge={bridge} row={selected} snapshot={snapshot} />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 pb-6 text-center text-sm text-foreground-secondary">
          <ScanLine aria-hidden="true" className="size-6" />
          <p>{emptyMessage}</p>
        </div>
      )}
      {showSources && (
        <details className="shrink-0 border-t border-border px-4 py-2 text-xs text-foreground-secondary">
          <summary className="cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
            {copy.sourceLegend}
          </summary>
          <ul aria-label={copy.sourceLegend} className="mt-2 flex flex-wrap gap-x-4 gap-y-2 pb-1">
            {Object.entries(TRACKING_SOURCE_APPEARANCES).map(([source, appearance]) => (
              <li className="flex items-center gap-1.5" key={source}>
                <span aria-hidden="true" className="size-2 rounded-sm" style={{ backgroundColor: appearance.color }} />
                {appearance.label}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
